import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../config/Config';

const appId = FIREBASE_CONFIG.projectId;

export async function fetchDocumentMeta(documentId) {
  const readCandidates = [];
  if (String(appId || '').trim()) {
    readCandidates.push(doc(db, 'artifacts', appId, 'public', 'data', 'documents', documentId));
  }
  readCandidates.push(doc(db, 'documents', documentId));

  for (const candidateRef of readCandidates) {
    try {
      const snap = await getDoc(candidateRef);
      if (!snap.exists()) continue;
      const data = snap.data() || {};
      return {
        id: snap.id,
        title: data.title || data.name || 'Documento',
        type: data.type || '',
        status: data.status || '',
        parentDocumentId: data.parentDocumentId || null,
      };
    } catch { /* continue */ }
  }
  return null;
}

export function buildClientNotesRef(clientId) {
  return collection(db, 'artifacts', appId, 'public', 'data', 'clients', clientId, 'notes');
}

export async function fetchClientWorkspacesForNotes(clientId) {
  const ref = collection(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces');

  const directSnap = await getDocs(query(ref, where('clientId', '==', clientId), limit(80)));
  const direct = directSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

  const sampleSnap = await getDocs(query(ref, limit(200)));
  const sample = sampleSnap.docs
    .map((d) => ({ id: d.id, ...(d.data() || {}) }))
    .filter((w) => {
      const refs = Array.isArray(w?.partiesRefs) ? w.partiesRefs : [];
      return refs.some((p) => String(p?.clientId || '').trim() === clientId);
    });

  const merged = new Map();
  [...direct, ...sample].forEach((w) => {
    if (!w?.id) return;
    merged.set(String(w.id), w);
  });

  const out = Array.from(merged.values());
  out.sort((a, b) => {
    const av = a?.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : 0;
    const bv = b?.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : 0;
    return bv - av;
  });

  return out;
}

export async function fetchNotesPage(notesRef, pageSize, lastDocSnap = null) {
  let q;
  if (lastDocSnap) {
    q = query(notesRef, orderBy('createdAt', 'desc'), startAfter(lastDocSnap), limit(pageSize));
  } else {
    q = query(notesRef, orderBy('createdAt', 'desc'), limit(pageSize));
  }
  const snap = await getDocs(q);
  const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}), __ref: d.ref }));
  const lastDoc = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
  const hasMore = snap.docs.length === pageSize;
  return { items, lastDoc, hasMore };
}

export async function toggleNotePinned(ref, nextPinned) {
  await updateDoc(ref, { isPinned: nextPinned });
}

export async function addNoteReply(ref, reply) {
  const { arrayUnion } = await import('firebase/firestore');
  await updateDoc(ref, { replies: arrayUnion(reply) });
}

export async function fetchDocumentsMetaBatch(documentIds, clientId) {
  const out = {};
  await Promise.all(
    documentIds.map(async (did) => {
      try {
        const refs = [];
        if (String(appId || '').trim()) refs.push(doc(db, 'artifacts', appId, 'public', 'data', 'documents', did));
        refs.push(doc(db, 'documents', did));

        for (const candidateRef of refs) {
          const snap = await getDoc(candidateRef);
          if (!snap.exists()) continue;
          const data = snap.data() || {};
          out[did] = {
            documentId: did,
            clientId: String(data?.clientId || data?.context?.primaryEntityId || clientId || '').trim(),
            title: String(data?.title || data?.name || data?.fileName || '').trim(),
            fileUrl: String(data?.fileUrl || data?.url || data?.webViewLink || data?.downloadUrl || '').trim(),
            url: String(data?.url || '').trim(),
            downloadUrl: String(data?.downloadUrl || '').trim(),
            mirror_url: String(data?.mirror_url || '').trim(),
            storagePath: String(data?.storagePath || '').trim(),
            storage_path: String(data?.storage_path || data?._metadata?.storage_path || '').trim(),
            storageEngine: data?.storageEngine || null,
            module: String(data?.module || data?.context?.module || '').trim(),
            type: String(data?.type || data?.contentData?.documentType || '').trim(),
            publicationSeriesId: String(data?.publicationSeriesId || data?.id || '').trim(),
            publicationVersion: Math.max(0, Number(data?.publicationVersion || 0) || 0),
            requiresConsent: Boolean(data?.requiresConsent),
            consentStatus: String(data?.consentStatus || '').trim(),
            status: String(data?.status || '').trim(),
          };
          break;
        }
      } catch { /* ignore */ }
    })
  );
  return out;
}

export async function addClientNoteToRef(notesRef, payload) {
  await addDoc(notesRef, {
    ...payload,
    createdAt: serverTimestamp(),
  });
}
